import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { catchError, map, of } from 'rxjs';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FileUploadAnswer, Utils } from '../../../../../common/utils';
import { OtrasFormDefinition } from './form-control/form-definition';
import { environment } from '../../../../../../environments/environment';
import { StorageManager } from '../../../../../common/storage-manager.class';
import {
    FormType,
    type FormField,
    DropdownData,
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { UsuariosApiService } from '../../../../../service/api/usuarios-api.service';
import type { TareaOtraModel } from 'src/app/models/tareas/tarea-otra.model';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { SectorModel } from 'src/app/models/form-common/sector.model';
import type { FincasModel } from 'src/app/models/form-common/fincas.module';
import type { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import type { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import type { ClienteModel } from 'src/app/models/cliente.model';
import {
    build,
    buildWithParams,
    getDropdownSelectedArray,
    pool,
    ReactiveCascadeController,
    type ReactiveCascadeResetTarget,
    ReactiveDropdownSyncController,
    ReactiveSelectionResolver,
    setDropdownSelectedFromArray,
} from '@fitogest/reactive-form-builder/utils';

type HydratedLocationValues = {
    id_finca?: string | null;
    id_sector?: string | null;
    ids_parcelas?: string | null;
};

type OtrosDropdownCatalogPhase = 'critical' | 'deferred' | 'lazy';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

@Component({
    selector: 'app-otros-form',
    standalone: false,
    templateUrl: './otros-form.component.html',
    styleUrls: ['./otros-form.component.scss'],
    providers: [MessageService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtrosFormComponent extends BaseForm<TareaOtraModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario genérico...';

    public appName = environment.appName;
    public hasClients = environment.features.hasClients;
    public clienteName = environment.features.clienteName;
    public userCanSeeClientes = environment.features.userCanSeeClientes
        ? true
        : StorageManager.getUser().rol === 'admin';
    public horaFechaFinActualPorDefecto = environment.features.horaFechaFinActualPorDefecto;
    public showTareasOtrosDuracion = environment.features.showTareasOtrosDuracion;
    public showTareasOtrosImagen = environment.features.showTareasOtrosImagen;
    public horaFechaInicioActualPorDefecto = environment.features.horaFechaInicioActualPorDefecto;
    public activateRequireHourForRolUser = this.appName === 'ava' ? StorageManager.getUser().rol !== 'admin' : false;
    public now = new Date();
    public day = this.now.getDate().toString().padStart(2, '0');
    public month = (this.now.getMonth() + 1).toString().padStart(2, '0');
    public year = this.now.getFullYear().toString();
    public seleccionados = 0;
    public form!: OtrasFormDefinition;
    public override model: TareaOtraModel = {};
    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.tareasApi.otras.GET;
    public reactiveFormRecordId = '';
    public initialPreviousData: TareaOtraModel | null = null;
    public idxMaquinaria = '';
    public adjunto: string | ArrayBuffer | null | undefined = null;
    public rolUser = StorageManager.getUser().rol;

    private readonly dropdownCatalogPhases: readonly OtrosDropdownCatalogPhase[] = ['critical', 'deferred', 'lazy'];

    private hydratedLocationValues: HydratedLocationValues = {};

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public formApi: FormCommonApiService,
        public clientesApi: ClientesApiService,
        public usersApi: UsuariosApiService,
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public tareasApi: TareasApiService,
        private readonly messageService: MessageService,
        public override router: Router,
        public http: HttpClient,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.otras.PUT,
            tareasApi.otras.POST,
            'tareas-otros',
            'Actualizar Tarea',
            'Realizar Tarea Genérica',
            'Duplicar Tarea',
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            this.model = this.initializeHostModel(this.toOtrosModel(this.getModel()));
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(this.model);

            if (this.horaFechaInicioActualPorDefecto) {
                this.model.fecha = `${this.day}/${this.month}/${this.year}`;
                this.updateHostModel(this.model);
            }

            await this.loadEventosYForm();
            this.form.model = this.toOtrosModel(this.form.model);
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario genérico.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TareaOtraModel>): void {
        const changedFieldName = String(event.changedField ?? '');
        const modelChanges = this.captureStableValueChange(event.value as TareaOtraModel);

        this.form.model = this.toOtrosModel({
            ...this.form.model,
            ...modelChanges,
        });
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<TareaOtraModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaOtraModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: lastChangedFieldName,
            changedFields: [String(lastChangedFieldName ?? '')],
            origin: 'user',
        });
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<TareaOtraModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaOtraModel>,
    ): void {
        const tag = String(lastChangedFieldName);

        this.clearHydratedLocationValues(tag);

        this.form.model = this.toOtrosModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (tag === 'hora_inicio' || tag === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (this.model.id_maquinaria == null) {
            this.model.id_maquinaria = null;
        }

        if (tag === 'id_finca') {
            this.clearOtrosCascade(['id_sector', 'ids_parcelas']);
        }

        if (tag === 'id_sector') {
            this.clearOtrosCascade(['ids_parcelas']);
        }

        if (tag === 'id_cliente') {
            this.reloadClientLocations();
        }

        if (tag === 'ids_trabajadores') {
            this.syncMaquinariaFromTrabajador();
        }

        this.countTrabajadores();
        this.countSuperficieParcelas();
        this.syncLocationSelectionsWithCurrentModel(tag);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareaOtraModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            if (event.type === 'dropdownDataResolved' || event.type === 'dropdownRefresh') {
                this.scheduleLocationSelectionSync();
            }

            return;
        }

        const currentHydratedLocationValues = this.createHydratedLocationValues(this.model as ReactiveFormValue<TareaOtraModel>);
        const patchedHydratedLocationValues = this.createHydratedLocationValues(event.value);

        const modelChanges = this.captureStableValueChange(event.value as TareaOtraModel);

        this.form.model = this.toOtrosModel({
            ...this.form.model,
            ...modelChanges,
        });
        this.syncModelFromForm();
        this.hydratedLocationValues = this.mergeHydratedLocationValues(
            patchedHydratedLocationValues,
            this.hydratedLocationValues,
            currentHydratedLocationValues,
        );
        this.updateHorasTotales();
        this.scheduleLocationSelectionSync();
    }

    public afterSend(): void {
        if (this.getType() === FormType.DUPLICATION) {
            window.location.reload();
        }
    }

    public formatoHorasMinutos(horaEntrada: string, horaSalida: string): string {
        const [hours = '', minutes = ''] = horaEntrada.split(':');

        return moment(`${horaSalida} 2021-04-05`, 'HH:mm YYYY-MM-DD')
            .subtract({ hours: +hours, minutes: +minutes })
            .format('HH:mm');
    }

    public countTrabajadores(): void {
        const selectedTrabajadores = this.getSelectedTrabajadores();

        setDropdownSelectedFromArray(this.form.trabajadores, selectedTrabajadores, true);
        this.seleccionados = selectedTrabajadores.length;
    }

    public countSuperficieParcelas(): void {
        const selectedParcelas = this.getSelectedParcelas();

        setDropdownSelectedFromArray(this.form.parcelas, selectedParcelas, true);
        this.model.parcelas_superficie = Utils.decimalFormat(this.sumSuperficieParcelas(selectedParcelas), 2, '.', ',', 3);
        this.form.model.parcelas_superficie = this.model.parcelas_superficie;
    }

    public selectAll(): void {
        this.countSuperficieParcelas();
    }

    public override getItemPosition(formFields: FormField<object>[], field: string): number {
        let position = 0;

        formFields.forEach((item, index) => {
            if (item.name === field) {
                position = index;
            }
        });

        return position;
    }

    public getObservaciones(event: string): void {
        this.model.observaciones = event;
        this.form.model.observaciones = event;
        this.formBuilderRef?.updateForm({ observaciones: event }, true);
    }

    public comprobarHoraInicioFin(model: TareaOtraModel): boolean {
        if (model.hora_inicio == null || model.hora_fin == null) {
            this.messageService.add({ summary: 'Error en horas', detail: 'Hora inicio y hora fin son necesarias' });

            return true;
        }

        const fechaInicio = moment(model.hora_inicio).isValid()
            ? moment(model.hora_inicio).format('HH:mm')
            : model.hora_inicio ?? '';
        const fechaFin = moment(model.hora_fin).isValid()
            ? moment(model.hora_fin).format('HH:mm')
            : model.hora_fin?.toString() ?? '';

        if (fechaInicio === fechaFin) {
            this.messageService.add({ summary: 'Error en horas', detail: 'Hora inicio y hora fin no pueden ser iguales' });

            return true;
        }

        const resultInicio = this.timeToMilliseconds(fechaInicio);
        const resultFin = this.timeToMilliseconds(fechaFin);

        if (resultInicio >= resultFin) {
            this.messageService.add({
                summary: 'Error en horas',
                detail: 'Hora inicio y hora fin no pueden ser iguales, ni la hora fin anterior a la hora de inicio',
            });

            return true;
        }

        return false;
    }

    public milliseconds(h: number, m: number, s: number): number {
        return ((h * 60 * 60 + m * 60 + s) * 1000);
    }

    public readUrl(input: HTMLInputElement): void {
        input.click();

        if (!input.files?.[0]) {
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            this.adjunto = event.target?.result;
        };
        reader.readAsDataURL(input.files[0]);
        const request = this.http.post(environment.serverUrl + 'ws/seguimiento/upload_file.php', this.adjunto).subscribe((res: unknown) => {
            const file = res as FileUploadAnswer;

            this.model.adjunto = file.fileName;
            this.form.model.adjunto = file.fileName;
            this.formBuilderRef?.updateForm({ adjunto: file.fileName }, true);
            request.unsubscribe();
        });
    }

    public fileChange(event: Event): void {
        const input = event.target as HTMLInputElement;

        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0] ?? new File([], '');
        const formData = new FormData();

        formData.append('uploadFile', file, file.name);
        const headers = new HttpHeaders();

        headers.append('Content-Type', 'multipart/form-data');
        headers.append('Accept', 'application/json');

        const name = file.name.split('.');
        const ext = name[name.length - 1];
        const accepted = ['gif', 'jpeg', 'jpg', 'jiff', 'png', 'svg', 'tiff'];

        if (accepted.some((acceptedExt) => acceptedExt === ext)) {
            this.http.post<FileUploadAnswer>(
                `${environment.serverUrl}ws/productos/upload_file.php`,
                formData,
                { headers },
            ).pipe(
                map((data: FileUploadAnswer | null) => {
                    const res: Partial<FileUploadAnswer> = data ?? {};

                    this.model.adjunto = res.fileName ?? '';
                    this.form.model.adjunto = res.fileName ?? '';
                    this.formBuilderRef?.updateForm({ adjunto: res.fileName }, true);
                }),
                catchError((error: unknown) => {
                    console.log(error);

                    return of(null);
                }),
            ).subscribe();
        } else {
            alert('Formato de imágen no válido.');
            this.model.adjunto = '';
            this.form.model.adjunto = '';
            this.formBuilderRef?.updateForm({ adjunto: '' }, true);
        }
    }

    public verAdjunto(): void {
        const myWindow = window.open('about:blank', '_blank', 'width=600, height=600');

        myWindow?.location.assign(environment.serverUrl + 'tareas_otros/' + this.model.adjunto);
        myWindow?.focus();
    }

    public async getEventosAPI(): Promise<{
        dates: Date[];
        eventos: { start: string; title: string; hora_inicio: string; hora_fin: string }[];
    }> {
        const fechas: Date[] = [];
        let eventosRaw: { start: string; title: string; hora_inicio: string; hora_fin: string }[] = [];

        try {
            eventosRaw = await this.tareasApi.eventos.GET.toPromise() as unknown as {
                start: string;
                title: string;
                hora_inicio: string;
                hora_fin: string;
            }[];

            for (const event of eventosRaw) {
                const [day, month, year] = event.start.split('-').map(Number);

                if ([day, month, year].every(Number.isInteger)) {
                    fechas.push(new Date(year ?? 0, (month ?? 1) - 1, day ?? 1));
                }
            }
        } catch (error) {
            console.error('Error cargando eventos:', error);
        }

        return { dates: fechas, eventos: eventosRaw };
    }

    private async loadEventosYForm(): Promise<void> {
        const eventosData = environment.appName === 'detorres'
            ? await this.getEventosAPI()
            : { dates: [], eventos: [] };

        this.form = new OtrasFormDefinition(eventosData.dates, eventosData.eventos, this.getType(), this.model);
        this.form.addClientFieldForDuplicate();
        this.configureDeclarativeLocationDataSources();
        await this.initFormData();
        this.afterInitialDataLoad();
        this.form.rebuildReactiveForm();
        this.configureDeclarativeLocationDataSources();
    }


    private configureDeclarativeLocationDataSources(): void {
        this.form.setLocationDataSources({
            fincas: this.fincasApi.fincasGET,
            sectores: this.fincasApi.sectoresGET,
            parcelas: this.fincasApi.parcelasGET,
        });
    }

    private refreshDeclarativeLocationDropdowns(): void {
        this.formBuilderRef?.refreshDeclarativeDropdown('id_finca');
        this.formBuilderRef?.refreshDeclarativeDropdown('id_sector');
        this.formBuilderRef?.refreshDeclarativeDropdown('ids_parcelas');
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        try {
            await this.loadDropdownCatalogPhases();
            this.sortPinnedTareas();
            this.syncSelectedClientDropdown();
            this.syncLocationDropdownSelections();
            this.countTrabajadores();
            this.countSuperficieParcelas();

            if (this.form.fincas.filtered.length === 2 && this.getType() === FormType.CREATION) {
                const finca = this.form.fincas.filtered[1]?.value as { id?: string | number } | null | undefined;

                this.model.id_finca = finca?.id?.toString() ?? '';
                this.form.model.id_finca = this.model.id_finca;
            }

            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();

            return true;
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
            console.error('Error loading otros form data.', error);
            throw error;
        }
    }

    private async loadDropdownCatalogPhases(): Promise<void> {
        for (const phase of this.dropdownCatalogPhases) {
            await this.loadDropdownCatalogPhase(phase);
        }
    }

    private async loadDropdownCatalogPhase(phase: OtrosDropdownCatalogPhase): Promise<void> {
        const catalogFields = this.form.formFields.dropdownCatalogs?.[phase] ?? [];
        const builds = catalogFields
            .map((fieldName) => this.createDropdownCatalogBuild(String(fieldName)))
            .filter((catalogBuild): catalogBuild is (resolve: (ok: boolean) => void) => void => catalogBuild !== null);

        if (builds.length === 0) {
            return;
        }

        await pool(builds, () => this.refreshReactiveDropdownMappings());
    }

    private createDropdownCatalogBuild(fieldName: string): ((resolve: (ok: boolean) => void) => void) | null {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente)
            || String(StorageManager.getClient()?.id ?? '');

        if (fieldName === 'id_cliente') {
            return build(this.clientesApi.clientesGET, this.form.clientes, 'nombre');
        }

        if (fieldName === 'id_finca' || fieldName === 'finca') {
            return selectedClientId
                ? buildWithParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.fincas, 'nombre', true)
                : build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true);
        }

        if (fieldName === 'id_sector' || fieldName === 'sector') {
            return selectedClientId
                ? buildWithParams(this.fincasApi.sectoresGET, { id_cliente: selectedClientId }, this.form.sectores, 'nombre', true)
                : build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true);
        }

        if (fieldName === 'ids_parcelas' || fieldName === 'parcelas') {
            return selectedClientId
                ? buildWithParams(this.fincasApi.parcelasGET, { id_cliente: selectedClientId }, this.form.parcelas, 'nombre', true)
                : build(this.fincasApi.parcelasGET, this.form.parcelas, 'nombre', true);
        }

        if (fieldName === 'id_maquinaria') {
            return build(
                this.trabajadoresApi.maquinariaGET,
                this.form.maquinarias,
                (it: MaquinariaModel) => `${it.nombre} ${it.empresa ? ` - (${it.empresa || '...'})` : ''}`,
                true,
            );
        }

        if (fieldName === 'ids_trabajadores') {
            return build(
                this.trabajadoresApi.trabajadoresGET,
                this.form.trabajadores,
                (it: MantenimientoModel) => it.nombre + ' ' + (it.apellidos || '') + (it.maquinaria
                    ? ' - (' + (it.maquinaria || '...') + ')'
                    : ''),
                true,
                true,
            );
        }

        if (fieldName === 'id_tarea') {
            return build(this.tareasApi.listaTareas.GET, this.form.tareas, 'nombre');
        }

        if (fieldName === 'id_producto') {
            return build(this.productosApi.productos.GET, this.form.productos, 'nombre');
        }

        if (fieldName === 'ids_asignados') {
            return environment.features.showAssignUserTasksFitosanitario || environment.features.showAssignUserInAllTasks
                ? build(this.usersApi.usuariosGET, this.form.users, 'nombre')
                : null;
        }

        return null;
    }

    private afterInitialDataLoad(): void {
        if (this.horaFechaFinActualPorDefecto && this.getType() !== FormType.EDITION) {
            const fechaActual = new Date();

            if (this.appName !== 'sirga') {
                this.model.fecha = this.formatDateForForm(fechaActual);
                this.form.model.fecha = this.model.fecha;
            }

            this.model.hora_fin = this.formatTimeForForm(fechaActual);
            this.form.model.hora_fin = this.model.hora_fin;
        }

        for (const row of this.form.maquinarias.filtered) {
            if (!row.value?.fecha_ultima_inspeccion) {
                continue;
            }

            const auxFechaCad = (row.value.fecha_ultima_inspeccion as string).split('/');

            if (parseInt(auxFechaCad[0] ?? '', 10) >= 2020) {
                auxFechaCad[2] = (parseInt(auxFechaCad[2] ?? '', 10) + 3).toString();
            } else {
                auxFechaCad[2] = (parseInt(auxFechaCad[2] ?? '', 10) + 5).toString();
            }

            const fechaCaducidad = auxFechaCad[0] + '/' + auxFechaCad[1] + '/' + auxFechaCad[2];

            if (moment(fechaCaducidad, 'DD/MM/YYYY').isBefore(moment())) {
                row.label = '** ' + row.label;
            } else if (
                moment(fechaCaducidad, 'DD/MM/YYYY').isSameOrAfter(moment()) &&
                moment(fechaCaducidad, 'DD/MM/YYYY').isBefore(moment().add(3, 'months'))
            ) {
                row.label = '* ' + row.label;
            }
        }

        if (environment.features.showTareasMto && this.rolUser === 'user') {
            this.model.prioridad = 'Realizada';
            this.form.model.prioridad = 'Realizada';
        }
    }

    private updateHorasTotales(): void {
        if (!environment.features.showTareasMto &&
            !environment.features.tareasHorasTotales &&
            !environment.features.showTareasOtrosDuracion) {
            return;
        }

        const diferencia = this.form.calculateHorasTotales();

        if (this.model.horas_totales === diferencia && this.form.model.horas_totales === diferencia) {
            return;
        }

        this.model.horas_totales = diferencia;
        this.form.model.horas_totales = diferencia;
        this.formBuilderRef?.updateForm({ horas_totales: diferencia }, false);
    }

    private clearOtrosCascade(fieldNames: string[]): void {
        const resetTargets = fieldNames.map((fieldName): ReactiveCascadeResetTarget<TareaOtraModel> => ({
            fieldName,
            value: null,
        }));
        const { updates } = ReactiveCascadeController.createResetPatch<TareaOtraModel>(resetTargets);

        this.form.model = this.toOtrosModel({
            ...this.form.model,
            ...updates,
        });
        this.syncModelFromForm();
        this.formBuilderRef?.updateForm(updates, false);
        this.refreshReactiveDropdownMappings();
    }

    private reloadClientLocations(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        if (selectedClientId.length === 0) {
            return;
        }

        this.clearOtrosCascade(['id_finca', 'id_sector', 'ids_parcelas']);
        this.refreshDeclarativeLocationDropdowns();
        this.refreshReactiveDropdownMappings();
    }



    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario genérico: ' + error.message;
        }

        return 'No se ha podido cargar el formulario genérico. Revisa la conexión e inténtalo de nuevo.';
    }

    private refreshReactiveDropdownMappings(): void {
        ReactiveDropdownSyncController.refreshMappings({ formBuilder: this.formBuilderRef });
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toOtrosModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toOtrosModel(
        sourceModel: ReactiveFormValue<TareaOtraModel> | Partial<TareaOtraModel> | Record<string, unknown>,
    ): TareaOtraModel {
        const normalizedModel: Record<string, TareaOtraModel[keyof TareaOtraModel]> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value as TareaOtraModel[keyof TareaOtraModel];
            }
        }

        return normalizedModel as TareaOtraModel;
    }

    private createHydratedLocationValues(value: Partial<TareaOtraModel> | ReactiveFormValue<TareaOtraModel>): HydratedLocationValues {
        const values: HydratedLocationValues = {};
        const fincaId = this.toOptionalStringLocationValue(value['id_finca']);
        const sectorId = this.toOptionalStringLocationValue(value['id_sector']);
        const parcelaIds = this.toOptionalStringLocationValue(value['ids_parcelas']);

        if (fincaId !== undefined) {
            values.id_finca = fincaId;
        }

        if (sectorId !== undefined) {
            values.id_sector = sectorId;
        }

        if (parcelaIds !== undefined) {
            values.ids_parcelas = parcelaIds;
        }

        return values;
    }

    private mergeHydratedLocationValues(...sources: HydratedLocationValues[]): HydratedLocationValues {
        const values: HydratedLocationValues = {};
        const fincaId = this.getFirstDefinedLocationValue(sources.map((source) => source.id_finca));
        const sectorId = this.getFirstDefinedLocationValue(sources.map((source) => source.id_sector));
        const parcelaIds = this.getFirstDefinedLocationValue(sources.map((source) => source.ids_parcelas));

        if (fincaId !== undefined) {
            values.id_finca = fincaId;
        }

        if (sectorId !== undefined) {
            values.id_sector = sectorId;
        }

        if (parcelaIds !== undefined) {
            values.ids_parcelas = parcelaIds;
        }

        return values;
    }

    private getFirstDefinedLocationValue(values: readonly (string | null | undefined)[]): string | null | undefined {
        return values.find((value) => value !== undefined);
    }

    private getFirstSelectedLocationValue(...values: unknown[]): unknown {
        return values.find((value) => ReactiveSelectionResolver.extractIdList(value).length > 0);
    }

    private clearHydratedLocationValues(fieldName: string): void {
        if (fieldName === 'id_finca') {
            this.hydratedLocationValues = {};
            return;
        }

        if (fieldName === 'id_sector') {
            delete this.hydratedLocationValues.id_sector;
            delete this.hydratedLocationValues.ids_parcelas;
            return;
        }

        if (fieldName === 'ids_parcelas') {
            delete this.hydratedLocationValues.ids_parcelas;
        }
    }

    private toOptionalStringLocationValue(value: unknown): string | null | undefined {
        if (value === null) {
            return null;
        }

        if (value === undefined || value === '') {
            return undefined;
        }

        const ids = ReactiveSelectionResolver.extractIdList(value);

        return ids.length > 0 ? ids.join(';') : undefined;
    }

    private scheduleLocationSelectionSync(): void {
        setTimeout(() => {
            this.syncLocationDropdownSelections();
            this.countSuperficieParcelas();
        });
    }

    private syncSelectedClientDropdown(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente ?? this.model.id_cliente)
            || String(StorageManager.getClient()?.id ?? '');

        if (selectedClientId.length === 0) {
            return;
        }

        this.model.id_cliente = selectedClientId;
        this.form.model.id_cliente = selectedClientId;
        this.syncDropdownSelection(
            'id_cliente',
            this.form.clientes as DropdownData<ClienteModel>,
            selectedClientId,
            false,
        );
    }

    private syncLocationSelectionsWithCurrentModel(changedFieldName: string): void {
        if (changedFieldName === 'id_finca' || changedFieldName === 'id_sector' || changedFieldName === 'id_cliente') {
            return;
        }

        this.syncLocationDropdownSelections();
    }

    private syncLocationDropdownSelections(): void {
        this.syncDropdownSelection(
            'id_finca',
            this.form.fincas as DropdownData<FincasModel>,
            this.getFirstSelectedLocationValue(
                this.form.model.id_finca,
                this.model.id_finca,
                this.hydratedLocationValues.id_finca,
            ),
            false,
        );
        this.syncDropdownSelection(
            'id_sector',
            this.form.sectores as DropdownData<SectorModel>,
            this.getFirstSelectedLocationValue(
                this.form.model.id_sector,
                this.model.id_sector,
                this.hydratedLocationValues.id_sector,
            ),
            this.form.showTareasOtrosMultiple,
        );
        this.syncDropdownSelection(
            'ids_parcelas',
            this.form.parcelas as DropdownData<ParcelasModel>,
            this.getFirstSelectedLocationValue(
                this.form.model.ids_parcelas,
                this.model.ids_parcelas,
                this.hydratedLocationValues.ids_parcelas,
            ),
            true,
        );
    }

    private syncDropdownSelection<T extends { id?: string | number | null }>(
        fieldName: string,
        dropdownData: DropdownData<T>,
        rawValue: unknown,
        isMultiple: boolean,
    ): void {
        ReactiveDropdownSyncController.syncSelection<TareaOtraModel, T>({
            formBuilder: this.formBuilderRef,
            fieldName,
            dropdown: dropdownData,
            rawValue,
            multiple: isMultiple,
        });
    }

    private getReactiveValueId(value: unknown): string {
        return String(ReactiveSelectionResolver.extractId(value) ?? '');
    }

    private formatDateForForm(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString();

        return `${day}/${month}/${year}`;
    }

    private formatTimeForForm(date: Date): string {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }

    private syncMaquinariaFromTrabajador(): void {
        const selectedList = this.getSelectedTrabajadores();

        if (selectedList.length !== 1 || !selectedList[0]?.id_maquina) {
            return;
        }

        this.model.id_maquinaria = selectedList[0].id_maquina ?? '';
        this.form.model.id_maquinaria = this.model.id_maquinaria;
        this.formBuilderRef?.updateForm({ id_maquinaria: this.model.id_maquinaria }, true);
    }

    private sortPinnedTareas(): void {
        const pinned = ['ENMACETADO', 'CORTE MAQUINÁRIA', 'CORTE MANUAL'];

        this.form.tareas.filtered = [...this.form.tareas.filtered].sort((a, b) => {
            const aIdx = pinned.indexOf((a.label ?? '').toUpperCase());
            const bIdx = pinned.indexOf((b.label ?? '').toUpperCase());

            if (aIdx !== -1 && bIdx !== -1) {
                return aIdx - bIdx;
            }

            if (aIdx !== -1) {
                return -1;
            }

            if (bIdx !== -1) {
                return 1;
            }

            return (a.label ?? '').localeCompare(b.label ?? '');
        });
    }

    private getSelectedParcelas(): ParcelasModel[] {
        const rawValue = this.getFirstSelectedLocationValue(
            this.formBuilderRef?.model?.get('ids_parcelas')?.value,
            this.form.model.ids_parcelas,
            this.model.ids_parcelas,
            this.hydratedLocationValues.ids_parcelas,
            getDropdownSelectedArray(this.form.parcelas),
        );

        return this.getSelectedDropdownItems(this.form.parcelas, rawValue);
    }

    private getSelectedTrabajadores(): MantenimientoModel[] {
        const rawValue = this.formBuilderRef?.model?.get('ids_trabajadores')?.value ??
            this.model.ids_trabajadores ??
            getDropdownSelectedArray(this.form.trabajadores);

        return this.getSelectedDropdownItems(this.form.trabajadores as DropdownData<MantenimientoModel>, rawValue);
    }

    private getSelectedDropdownItems<T extends { id?: string | number | null }>(dropdownData: DropdownData<T>, rawValue: unknown): T[] {
        return ReactiveSelectionResolver.resolveSelectedEntities(dropdownData, rawValue);
    }

    private sumSuperficieParcelas(parcelas: ParcelasModel[]): number {
        return parcelas
            .filter((it) => it && (it.superficie_cultivada || it.superficie_sigpac))
            .map((it) => parseFloat(((it.superficie_cultivada ?? 0) || (it.superficie_sigpac || 0)).toString().replace(',', '.')))
            .reduce((a, b) => a + b, 0);
    }

    private timeToMilliseconds(time: string): number {
        const [hours = '0', minutes = '0'] = time.split(':');

        return this.milliseconds(+hours, +minutes, 0);
    }
}
